import { FileText, Loader2, PenLine, Plus, Trash2, UsersRound } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';

function updatedLabel(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function SavedDrafts({ drafts, loading, deletingId, onResume, onDelete, onNew }) {
  const [pendingDelete, setPendingDelete] = useState(null);

  return (
    <section data-testid="saved-compose-drafts-list">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-serif font-medium text-foreground">Saved drafts</h2>
          <p className="text-sm text-muted-foreground" data-testid="saved-draft-count">{drafts.length} saved</p>
        </div>
        <Button className="rounded-full" onClick={onNew} data-testid="new-compose-draft-button"><Plus className="w-4 h-4 mr-1.5" /> New email</Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center" data-testid="saved-drafts-loading"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : drafts.length === 0 ? (
        <div className="py-16 text-center border-y border-border" data-testid="saved-drafts-empty-state">
          <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <p className="font-serif text-lg text-foreground">No saved drafts</p>
          <p className="text-sm text-muted-foreground mt-1">Start an email and choose Save draft.</p>
        </div>
      ) : (
        <div className="divide-y divide-border border-y border-border">
          {drafts.map(draft => (
            <article key={draft.id} className="py-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`saved-draft-row-${draft.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-foreground truncate" data-testid={`saved-draft-subject-${draft.id}`}>{draft.subject || '(no subject)'}</p>
                <p className="text-sm text-muted-foreground truncate">To: {(draft.recipients || []).join(', ') || 'No recipients yet'}</p>
                <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground mt-1">
                  <span>{updatedLabel(draft.updated_at)}</span>
                  {((draft.cc_recipients || []).length + (draft.bcc_recipients || []).length) > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <UsersRound className="w-3 h-3" />
                      <span data-testid={`saved-draft-cc-count-${draft.id}`}>Cc {(draft.cc_recipients || []).length}</span>
                      <span aria-hidden="true">·</span>
                      <span data-testid={`saved-draft-bcc-count-${draft.id}`}>Bcc {(draft.bcc_recipients || []).length}</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => onResume(draft)} data-testid={`resume-saved-draft-${draft.id}`}><PenLine className="w-3.5 h-3.5 mr-1" /> Resume</Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-[#A3503B]" onClick={() => setPendingDelete(draft)} aria-label={`Delete draft ${draft.subject || 'without subject'}`} data-testid={`delete-saved-draft-${draft.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </article>
          ))}
        </div>
      )}

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={open => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent className="max-w-md" data-testid="delete-saved-draft-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete this saved draft?</AlertDialogTitle>
            <AlertDialogDescription>This removes only the Email Hub draft. No email is sent or changed in Apple Mail.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingId)} data-testid="cancel-delete-saved-draft-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(deletingId)}
              className="rounded-full bg-[#A3503B] text-white hover:bg-[#843F30]"
              onClick={event => {
                event.preventDefault();
                if (!pendingDelete || deletingId) return;
                const draftToDelete = pendingDelete;
                Promise.resolve(onDelete(draftToDelete)).finally(() => setPendingDelete(null));
              }}
              data-testid="confirm-delete-saved-draft-button"
            >
              {deletingId ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />} Delete draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}